import React, {ClipboardEvent, KeyboardEvent, useState} from "react";
import {
    createControl,
    removeInvisibleCharsOnPaste,
    updateControl,
    validateControl,
    validateForm
} from "../../../domain/validation/validation";
import {getIsChina, getWavesUserEmail} from "../../../domain/hidden-data-provider";
import {getSimpleCaptchaValidationError} from "../../../domain/captcha/simple-captcha";
import {useAppSelector} from "../../daily-deals/DAL/store";
import {IFormControl} from "../../../ui/forms/interfaces";
import {IServerValidation} from "../../../domain/validation/interfaces";
import {
    getRecaptcha2Error,
    getRecaptchaToken,
    renderRecaptcha2,
    resetRecaptcha2
} from "../../../domain/captcha/recaptcha-provider";
import Preloader from "../../../ui/svg/common/preloader";
import ChinaCaptchaField from "../../../ui/forms/fields/captcha-china-field";
import Recaptcha3Text from "../../../domain/captcha/recaptcha3-terms";
import ValidationErrors from "../../../domain/validation/server-errors/validation-errors";
import {sendCodeEmail} from "../DAL/data-service";
import {GetProductsListInputParams} from "../services/logic-helper";

/**
 * Send Email Code
 */
const SendEmailCode = () => {

    const UserState_CableCount= useAppSelector((store) => store.UserState_CableCount);
    const UserState_AllMandatoryAdded = useAppSelector((store) => store.UserState_AllMandatoryAdded);
    const UserState_SelectedProducts = useAppSelector((store) => store.UserState_SelectedProducts);

    const emailControl = createControl('email', getWavesUserEmail(), {
        required: true,
        email: true,
    });

    const simpleCaptchaControl = createControl('simpleCaptcha', '', {
        required: true,
        requiredMessage: getSimpleCaptchaValidationError(),
        length: 5,
    });

    const [email, setEmail] = useState<IFormControl>(emailControl);
    const [allMandatoryError, setAllMandatoryError] = useState("");

    const [isChina] = useState(getIsChina());
    const [isLoading, setIsLoading] = useState(false);
    const [response, setResponse] = useState<IServerValidation|null>();
    const [okMessageShown, setOkMessageShown] = useState(false);

    // simple captcha - used for chine
    const [simpleCaptchaCode, setSimpleCaptchaCode] = useState<IFormControl>(simpleCaptchaControl);
    const [simpleCaptchaAttempts, setSimpleCaptchaAttempts] = useState(0);

    // google recaptcha version 2 & 3
    const [recaptchaVersion, setRecaptchaVersion] = useState(3);
    const [recaptcha2WidgetID, setRecaptcha2WidgetID] = useState(0);

    // captcha: common
    const [captchaError, setCaptchaError] = useState('');

    /**
     * if recaptcha 3 returns low score (the limit is defined in kentico settings),
     * then recaptcha 2 fallback should be rendered inside <div id="recaptcha"></div>
     */
    const renderCaptcha2Fallback = () => {
        if(!window.grecaptcha) return;

        window.grecaptcha.ready(async () => {
            const widgetID = await renderRecaptcha2('recaptcha');
            setRecaptcha2WidgetID(widgetID);
        });
    };
    /**
     * empty errors on click
     */
    const handleFieldClick = () =>{
        setResponse(null);
        setCaptchaError('');
    };

    const sendDealerCode = async () => {
        if(!UserState_AllMandatoryAdded){
            setAllMandatoryError("Please select all mandatory items.");
            setTimeout(function(){
                setAllMandatoryError("");

            }, 5000);
            return;
        }
        // clear all messages
        setResponse(null);
        setOkMessageShown(false);

        // client side validation
        const isValid =
            isChina ?
                validateForm([
                    [email, setEmail],
                    [simpleCaptchaCode, setSimpleCaptchaCode],
                ]) :
                validateForm([
                    [email, setEmail],
                ]);

        if(!isValid) return;

        setIsLoading(true);

        let response = null;

        try {
            const captchaToken = !isChina ? await getRecaptchaToken(recaptchaVersion) : simpleCaptchaCode.value;

            if(!captchaToken){
                setCaptchaError(isChina ? getSimpleCaptchaValidationError() : getRecaptcha2Error());
                setIsLoading(false);
                return;
            }
            response = await sendCodeEmail(
                email.value,
                GetProductsListInputParams(UserState_SelectedProducts,UserState_CableCount),
                recaptchaVersion,
                captchaToken
            );

        }
        catch(ex){}

        setIsLoading(false);

        // all is well ---------------
        if(
            response &&
            response.statusCode === 200 &&
            response.data &&
            response.data.isValid){

            setOkMessageShown(true);
            return;
        }else{

        }

        // handle errors ---------

        if(isChina){

            // re-render simple captcha
            setSimpleCaptchaAttempts(simpleCaptchaAttempts + 1);
        }
        else{
            if(response?.data.relatedData === 'recaptcha-error') {
                setRecaptchaVersion(2);
                renderCaptcha2Fallback();
            }

            if(recaptchaVersion === 2){
                resetRecaptcha2();
            }
        }

        // render errors
        setResponse(response?.data);
    };

    /**
     * and enter / return keys
     */
    const handleEnter = async (evt: KeyboardEvent<HTMLInputElement>) => {
        if(evt.key === 'Enter') {
            await sendDealerCode();
        }
    };
    return (
        <div className="">
            <p className="text-sm md:w-2/3">To purchase this system from a <a href="/dealers" className="">local Waves
                dealer</a>, select the products you want above and click ‘Send Code’. You will then receive the system
                code to your email. Show this code to your local Waves dealer.
            </p>
            <div className="w-full mt-6 md:w-1/2">
                <div id="emailInput" className={`w-full bg-white border rounded-full inline-flex items-center px-1 py-1 ${ !email.isValid ? 'border-red' : '' }`} style={{
                    maxWidth: '420px',
                    width: '100%',
                }}>
                    <label className="flex-1 ml-4">
                        <input
                            id="emailInputEnter"
                            type="text"
                            className={`w-full outline-none`}
                            placeholder="Your E-mail…"
                            value={ email.value }
                            maxLength={ 80 }
                            onChange={ evt => {
                                const $target = evt.target as HTMLInputElement;
                                setEmail(updateControl(email, $target.value, !email.isValid));
                                setResponse(null); // empty server error
                                setCaptchaError('');
                            }}
                            onBlur={ evt => {
                                setEmail(validateControl(email));
                            }}
                            onClick={ handleFieldClick }
                            onKeyUp={ handleEnter }
                            onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                                setEmail(updateControl(email, removeInvisibleCharsOnPaste(evt)));
                            }}
                        />
                        <span className="sr-only">Your E-mail…</span>
                    </label>

                    {
                        !isLoading &&
                        <>
                            <button
                                type="button"
                                className={`${UserState_AllMandatoryAdded ? "bg-blue-200" : "bg-gray-200"} text-white px-6 py-2 ml-4 rounded-full`}
                                onClick={ sendDealerCode }>Send Code</button>
                        </>
                    }

                    {
                        isLoading &&
                        <div style={{
                            margin: '7px 0',
                        }}>
                            <Preloader width="25px" height="25px" color="#4f4f4f" />
                        </div>
                    }
                </div>
            </div>
            <div className="w-full md:w-1/2">


                {
                    !email.isValid &&
                    <div className="text-red text-sm mt-2" style={{
                        width: '420px',
                        maxWidth: '100%',
                    }}>{ email.error }</div>
                }
                {
                    allMandatoryError.length > 0 &&
                    <div className="text-red text-sm mt-2" style={{
                        width: '420px',
                        maxWidth: '100%',
                    }}>{ allMandatoryError }</div>
                }
                {
                    /* ------------- google recaptcha version 2 or 3 ------------- */
                    !isChina &&
                    <div id="recaptcha" className={`mb-4 flex justify-center recaptcha recaptcha-version-${ recaptchaVersion }`}/>
                }

                {
                    /* ------------- china simple captcha ------------- */
                    isChina &&
                    <div className="my-4" style={{
                        width: '270px',
                    }}>
                        <ChinaCaptchaField
                            control={ simpleCaptchaCode }
                            setSimpleCaptchaCode={ setSimpleCaptchaCode }
                            simpleCaptchaAttempts={ simpleCaptchaAttempts }
                        />
                    </div>
                }


                {
                    captchaError &&
                    <div className="error-box mt-4" data-id={ recaptcha2WidgetID }  role="alert">
                        <p>{ captchaError }</p>
                    </div>
                }

                {
                    !isChina && recaptchaVersion === 3 &&
                    <div className="w-full mt-4">
                        <Recaptcha3Text />
                    </div>
                }
            </div>
            <div  className="w-full md:w-1/2 mb-6">
                {
                    okMessageShown && (
                        <div className="ok-box mt-4"  role="alert">
                            <p>Email sent successfully.</p>
                        </div>
                    )
                }

                {
                    response && !response.isValid && (
                        <div className="error-box mt-4"  role="alert">
                            <ValidationErrors errors={ response.errors } />
                        </div>
                    )
                }
            </div>
        </div>
    );
};

export default SendEmailCode;
